import type {
  UsageStats,
  UsageSeries,
  KeyUsageResponse,
  AiStatus,
  ApiKeySummary,
  ApiKeyEnvironment,
  ApiError,
  OrgPlansResponse,
  PlanInterval,
  InvoiceDTO,
  AuditLogPage,
  AuditLogMeta,
  BillingCountriesResponse,
  OverageInfo,
  ByokSettings,
  ByokProviderRecord,
  ByokProviderWrite,
} from "@currencycore/shared";
import { AI_CREDIT_PACKAGES, API_ROUTES, PLAN_LIMITS, type PlanTier } from "@currencycore/shared";
import {
  MOCK_ENABLED,
  createMockKey,
  listMockKeys,
  mockDemoKey,
  mockAiRequests,
  mockMcpRequests,
  mockOrgPlans,
  mockUsage,
  mockUsageDaily,
  revokeMockKey,
} from "./mock.ts";

const BASE_URL =
  (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ??
  "https://api.currency-core.com";

// Silently falling back to the prod URL is safe for local `vite dev` (no env
// file needed) but dangerous in a built/deployed bundle — a deploy that forgot
// to set VITE_API_BASE_URL would point at prod from any environment. Warn
// loudly (not throw — a public-route page shouldn't hard-fail on this) so it
// shows up in build logs / browser console instead of failing silently.
if (!import.meta.env["VITE_API_BASE_URL"] && import.meta.env.PROD) {
  console.error(
    "[CurrencyCore] VITE_API_BASE_URL is not set in this production build — " +
      `falling back to ${BASE_URL}. Set it explicitly for this deploy target.`,
  );
}

/**
 * Base URL the dashboard uses for every API call. Re-exported so the Playground
 * can hit the public API directly (with a Bearer demo key + session cookie)
 * without going through `apiFetch` (which forces a JSON Content-Type).
 */
export const API_BASE = BASE_URL;

// ---------------------------------------------------------------------------
// Core fetch helper
// ---------------------------------------------------------------------------

class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(body.error.message);
    this.name = "ApiRequestError";
  }
}

/**
 * User-facing message for a caught API/network error. 4xx errors carry a
 * deliberately-authored, safe message from the backend (see lib/errors.ts) so
 * those are shown as-is. Anything else (5xx, network failure, non-API throw)
 * gets a generic message instead of surfacing raw error text.
 */
export function friendlyErrorMessage(err: unknown, fallback = "Something went wrong. Please try again."): string {
  if (err instanceof ApiRequestError && err.status >= 400 && err.status < 500) {
    return err.body.error.message || fallback;
  }
  return fallback;
}

async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({
      error: { code: "internal_error", message: res.statusText },
    }))) as ApiError;
    throw new ApiRequestError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Typed API calls
// ---------------------------------------------------------------------------

export function getOrgUsage(orgId: string): Promise<UsageStats> {
  if (MOCK_ENABLED) return Promise.resolve(mockUsage(orgId));
  const path = API_ROUTES.orgUsage.replace(":orgId", orgId);
  return apiFetch<UsageStats>(path);
}

/** Real per-day request series (incl. the MCP subset) for the usage chart. */
export function getOrgUsageDaily(orgId: string, days: number): Promise<UsageSeries> {
  if (MOCK_ENABLED) return Promise.resolve(mockUsageDaily(orgId, days));
  const path = `${API_ROUTES.orgUsageDaily.replace(":orgId", orgId)}?days=${days}`;
  return apiFetch<UsageSeries>(path);
}

/** Per-API-key + per-endpoint request counts for a month (defaults to current). */
export function getOrgUsageKeys(orgId: string, period?: string): Promise<KeyUsageResponse> {
  if (MOCK_ENABLED) {
    return Promise.resolve({
      organizationId: orgId,
      period: period ?? new Date().toISOString().slice(0, 7),
      keys: [],
      byEndpoint: { convert: 0, rates: 0, ppp: 0, ai: 0 },
    });
  }
  const q = period ? `?period=${period}` : "";
  const path = `${API_ROUTES.orgUsageKeys.replace(":orgId", orgId)}${q}`;
  return apiFetch<KeyUsageResponse>(path);
}

export function getOrgPlans(orgId: string): Promise<OrgPlansResponse> {
  if (MOCK_ENABLED) return Promise.resolve(mockOrgPlans(orgId));
  return apiFetch<OrgPlansResponse>(`/orgs/${orgId}/plans`);
}

/** Pay the org's outstanding overage balance now. Returns a hosted payment link
 *  when Dodo needs the customer to complete it, `paid: true` when it
 *  auto-collected, or `pending: true` when the provider never answered — that
 *  charge may still be live, so it must NOT be retried. */
export function payOverage(orgId: string): Promise<{ paid: boolean; pending?: boolean; payUrl: string | null; amountCents: number }> {
  return apiFetch<{ paid: boolean; pending?: boolean; payUrl: string | null; amountCents: number }>("/billing/overage/pay", {
    method: "POST",
    body: JSON.stringify({ organizationId: orgId }),
  });
}

/** Over-usage billing state (pending + owed overage, next-billing info from Dodo). */
export function getOverage(orgId: string): Promise<OverageInfo> {
  if (MOCK_ENABLED) {
    return Promise.resolve({
      available: false, pendingCents: 0, owedCents: 0, rateCents: null, unit: 1000,
      interval: "monthly", billedHow: "renewal", nextBillingDate: null,
      nextBillingBaseCents: null, currency: "USD", requests: 0, limit: null,
    });
  }
  return apiFetch<OverageInfo>(API_ROUTES.orgOverage.replace(":orgId", orgId));
}

// ---------------------------------------------------------------------------
// Customer audit log (Business+ / entitled orgs)
// ---------------------------------------------------------------------------

export interface AuditFilters {
  categories?: string[];
  events?: string[];
  actor?: string;
  /** ISO datetime lower bound. */
  from?: string;
  /** ISO datetime upper bound. */
  to?: string;
  q?: string;
}

function auditQueryString(filters: AuditFilters, extra?: Record<string, string>): string {
  const p = new URLSearchParams();
  (filters.categories ?? []).forEach((c) => p.append("category", c));
  (filters.events ?? []).forEach((e) => p.append("event", e));
  if (filters.actor) p.set("actor", filters.actor);
  if (filters.from) p.set("from", filters.from);
  if (filters.to) p.set("to", filters.to);
  if (filters.q) p.set("q", filters.q);
  for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function getAuditLogs(
  orgId: string,
  filters: AuditFilters = {},
  cursor?: string | null,
  limit = 50,
): Promise<AuditLogPage> {
  if (MOCK_ENABLED) return Promise.resolve({ events: [], nextCursor: null });
  const extra: Record<string, string> = { limit: String(limit) };
  if (cursor) extra["cursor"] = cursor;
  return apiFetch<AuditLogPage>(`/orgs/${orgId}/audit-logs${auditQueryString(filters, extra)}`);
}

export function getAuditLogMeta(orgId: string): Promise<AuditLogMeta> {
  if (MOCK_ENABLED) return Promise.resolve({ actors: [], events: [], enabled: false, retentionDays: 0 });
  return apiFetch<AuditLogMeta>(`/orgs/${orgId}/audit-logs/meta`);
}

/** Download the filtered audit log as CSV/JSON (auth via cookie; blob to keep it). */
export async function exportAuditLog(
  orgId: string,
  filters: AuditFilters,
  format: "csv" | "json",
): Promise<void> {
  const url = `${BASE_URL}/orgs/${orgId}/audit-logs/export${auditQueryString(filters, { format })}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

export interface HardCapResponse {
  hardCapRequests: number | null;
  adminHardCapRequests: number | null;
  effectiveCap: number | null;
}

/** Set (or clear, with null) the org's own hard usage cap. */
export function setOrgHardCap(
  orgId: string,
  requests: number | null,
): Promise<HardCapResponse> {
  if (MOCK_ENABLED) {
    return Promise.resolve({
      hardCapRequests: requests,
      adminHardCapRequests: null,
      effectiveCap: requests,
    });
  }
  return apiFetch<HardCapResponse>(`/orgs/${orgId}/hard-cap`, {
    method: "PUT",
    body: JSON.stringify({ requests }),
  });
}

export interface ApiKeyCreatePayload {
  name: string;
  organizationId: string;
}

export interface ApiKeyCreatedResponse {
  key: string; // full key shown once
  summary: ApiKeySummary;
}

export function listApiKeys(orgId: string): Promise<ApiKeySummary[]> {
  if (MOCK_ENABLED) return Promise.resolve(listMockKeys(orgId));
  const path = API_ROUTES.apiKeys.replace(":orgId", orgId);
  return apiFetch<ApiKeySummary[]>(path);
}

export function createApiKey(
  orgId: string,
  payload: { name: string; environment?: ApiKeyEnvironment },
): Promise<ApiKeyCreatedResponse> {
  if (MOCK_ENABLED) {
    return Promise.resolve(createMockKey(orgId, payload.name, payload.environment ?? "live"));
  }
  const path = API_ROUTES.apiKeys.replace(":orgId", orgId);
  return apiFetch<ApiKeyCreatedResponse>(path, {
    method: "POST",
    body: JSON.stringify({ ...payload, organizationId: orgId }),
  });
}

export function revokeApiKey(orgId: string, keyId: string): Promise<void> {
  if (MOCK_ENABLED) {
    revokeMockKey(orgId, keyId);
    return Promise.resolve();
  }
  const path = `${API_ROUTES.apiKeys.replace(":orgId", orgId)}/${keyId}`;
  return apiFetch<void>(path, { method: "DELETE" });
}

/**
 * Decrypt and return the full plaintext key. Only works for keys created after
 * the encrypt-at-rest change (`summary.revealable === true`); older keys 409.
 */
export function revealApiKey(orgId: string, keyId: string): Promise<string> {
  if (MOCK_ENABLED) return Promise.resolve("cc_live_mock_full_key_value");
  const path = `${API_ROUTES.apiKeys.replace(":orgId", orgId)}/${keyId}/reveal`;
  return apiFetch<{ key: string }>(path).then((r) => r.key);
}

export interface DemoKeyResponse {
  /** Full plaintext sandbox key, e.g. `cc_demo_…`. */
  key: string;
  summary: ApiKeySummary;
  /** ISO timestamp the key expires (~1h out); used to rotate ahead of expiry. */
  expiresAt?: string;
}

/**
 * Get-or-create the CURRENT user's sandbox demo key for the Playground. The
 * backend rotates ahead, returning a fresh key when the old one is expired or
 * within ~5 min of expiry. Session-gated (requireMember) — any member can call.
 */
export function getDemoKey(orgId: string): Promise<DemoKeyResponse> {
  if (MOCK_ENABLED) return Promise.resolve(mockDemoKey(orgId));
  return apiFetch<DemoKeyResponse>(`/orgs/${orgId}/demo-key`, {
    method: "POST",
  });
}

export interface BillingPortalResponse {
  url: string;
}

export function getBillingPortalUrl(
  orgId: string,
): Promise<BillingPortalResponse> {
  if (MOCK_ENABLED) {
    window.alert(`Mock mode: would open the Dodo billing portal for ${orgId}.`);
    return Promise.resolve({ url: "#" });
  }
  return apiFetch<BillingPortalResponse>(API_ROUTES.billingPortal, {
    method: "POST",
    body: JSON.stringify({ organizationId: orgId }),
  });
}

export interface BillingCheckoutResult {
  /** Hosted-checkout URL (new subscription) — redirect the user here. */
  url?: string;
  /** Set when an existing subscription was changed in place (no redirect). */
  changed?: {
    effectiveAt: "immediately" | "next_billing_date";
    planId: string;
    endsAt?: string | null;
  };
}

export function createBillingCheckout(
  orgId: string,
  planTier: string,
  interval?: PlanInterval,
): Promise<BillingCheckoutResult> {
  if (MOCK_ENABLED) {
    window.alert(`Mock mode: would start a Dodo checkout for the "${planTier}" plan.`);
    return Promise.resolve({ url: "#" });
  }
  return apiFetch<BillingCheckoutResult>(API_ROUTES.billingCheckout, {
    method: "POST",
    body: JSON.stringify({ organizationId: orgId, plan: planTier, ...(interval ? { interval } : {}) }),
  });
}

/** Supported billing countries + the org's selected region (auto-detected on first load). */
export function getBillingCountries(orgId: string): Promise<BillingCountriesResponse> {
  if (MOCK_ENABLED) return Promise.resolve({ countries: [{ code: "US", name: "United States" }], selected: "US" });
  return apiFetch<BillingCountriesResponse>(`${API_ROUTES.billingCountries}?organizationId=${encodeURIComponent(orgId)}`);
}

/** Owner/admin: set the org's billing region (ISO alpha-2). */
export function setBillingRegion(orgId: string, country: string): Promise<{ country: string }> {
  if (MOCK_ENABLED) return Promise.resolve({ country });
  return apiFetch<{ country: string }>(API_ROUTES.billingRegion, {
    method: "PUT",
    body: JSON.stringify({ organizationId: orgId, country }),
  });
}

export interface CancelResponse {
  scheduled: boolean;
  plan: string;
  endsAt: string;
}

/** Cancel at period end → revert to Hobby when the paid period ends. */
export function cancelSubscription(orgId: string): Promise<CancelResponse> {
  if (MOCK_ENABLED) {
    return Promise.resolve({ scheduled: true, plan: "free", endsAt: new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth() + 1, 1)).toISOString() });
  }
  return apiFetch<CancelResponse>(API_ROUTES.billingCancel, {
    method: "POST",
    body: JSON.stringify({ organizationId: orgId }),
  });
}

/** Proration preview for an in-place plan change (amount Dodo would charge today). */
export interface PlanChangePreview {
  available: boolean;
  amountCents?: number | null;
  currency?: string | null;
  summary?: string | null;
}
export function previewPlanChange(
  orgId: string,
  planId: string,
  interval?: PlanInterval,
): Promise<PlanChangePreview> {
  if (MOCK_ENABLED) return Promise.resolve({ available: false });
  return apiFetch<PlanChangePreview>(API_ROUTES.billingPreview, {
    method: "POST",
    body: JSON.stringify({ organizationId: orgId, plan: planId, ...(interval ? { interval } : {}) }),
  });
}

/** Undo a scheduled cancellation before it takes effect. */
export function resumeSubscription(orgId: string): Promise<{ resumed: boolean }> {
  if (MOCK_ENABLED) return Promise.resolve({ resumed: true });
  return apiFetch<{ resumed: boolean }>(API_ROUTES.billingResume, {
    method: "POST",
    body: JSON.stringify({ organizationId: orgId }),
  });
}

export { ApiRequestError };

// ---------------------------------------------------------------------------
// MCP (Model Context Protocol) — connection status, usage, toggle, OAuth consent
// ---------------------------------------------------------------------------

export interface McpStatus {
  /** True once the org has connected to the MCP server at least once. */
  connected: boolean;
  connectedAt: string | null;
  /** Whether MCP access is currently enabled for the org. */
  enabled: boolean;
  /** YYYY-MM billing period the counts cover. */
  period: string;
  /** Total org requests this period. */
  requests: number;
  /** Subset of `requests` that came through the MCP server. */
  mcpRequests: number;
}

export function getMcpStatus(orgId: string): Promise<McpStatus> {
  if (MOCK_ENABLED) {
    return Promise.resolve({
      connected: true,
      connectedAt: new Date().toISOString(),
      enabled: true,
      period: new Date().toISOString().slice(0, 7),
      requests: mockUsage(orgId).requests,
      mcpRequests: mockMcpRequests(orgId),
    });
  }
  return apiFetch<McpStatus>(`/orgs/${orgId}/mcp`);
}

export function setMcpEnabled(orgId: string, enabled: boolean): Promise<{ enabled: boolean }> {
  if (MOCK_ENABLED) return Promise.resolve({ enabled });
  return apiFetch<{ enabled: boolean }>(`/orgs/${orgId}/mcp`, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
}

// ---------------------------------------------------------------------------
// AI endpoint — toggle + per-org AI usage (mirrors the MCP controls).
// ---------------------------------------------------------------------------

export function getAiStatus(orgId: string): Promise<AiStatus> {
  if (MOCK_ENABLED) {
    const usage = mockUsage(orgId);
    const aiLimit = usage.plan in PLAN_LIMITS ? PLAN_LIMITS[usage.plan as PlanTier].aiCreditLimit : null;
    const aiUsed = mockAiRequests(orgId);
    return Promise.resolve({
      enabled: true,
      period: new Date().toISOString().slice(0, 7),
      requests: usage.requests,
      aiRequests: aiUsed,
      aiLimit,
      aiCreditsUsed: aiUsed,
      aiCreditsRemaining: aiLimit == null ? null : Math.max(0, aiLimit - aiUsed),
      purchasedAiCredits: 0,
      globalEnabled: true,
      disabledMessage: null,
    });
  }
  return apiFetch<AiStatus>(API_ROUTES.orgAi.replace(":orgId", orgId));
}

export function setAiEnabled(orgId: string, enabled: boolean): Promise<{ enabled: boolean }> {
  if (MOCK_ENABLED) return Promise.resolve({ enabled });
  return apiFetch<{ enabled: boolean }>(API_ROUTES.orgAi.replace(":orgId", orgId), {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
}

// ---------------------------------------------------------------------------
// AI credit add-ons — one-time purchases (Dodo checkout).
// ---------------------------------------------------------------------------

export interface AiCreditPackageView {
  id: string;
  credits: number;
  priceCents: number;
}

/** Active AI credit add-on packages for the org's purchase UI. */
export function getAiCreditPackages(orgId: string): Promise<{ packages: AiCreditPackageView[] }> {
  if (MOCK_ENABLED) {
    return Promise.resolve({
      packages: AI_CREDIT_PACKAGES.map((p) => ({ id: p.id, credits: p.credits, priceCents: p.priceCents })),
    });
  }
  return apiFetch<{ packages: AiCreditPackageView[] }>(`/orgs/${orgId}/ai-credits/packages`);
}

/**
 * Start a one-time AI credit purchase. Returns the Dodo checkout URL to redirect
 * the buyer to (null in mock mode — no real checkout).
 */
export function purchaseAiCredits(orgId: string, packageId: string): Promise<{ url: string | null; purchaseId: string }> {
  if (MOCK_ENABLED) return Promise.resolve({ url: null, purchaseId: "mock_purchase" });
  return apiFetch<{ url: string | null; purchaseId: string }>(`/orgs/${orgId}/ai-credits/purchase`, {
    method: "POST",
    body: JSON.stringify({ packageId }),
  });
}

/** The org's invoices (each `hostedUrl` is Dodo's official invoice). Empty in mock mode. */
export function getOrgInvoices(orgId: string): Promise<{ invoices: InvoiceDTO[] }> {
  if (MOCK_ENABLED) return Promise.resolve({ invoices: [] });
  return apiFetch<{ invoices: InvoiceDTO[] }>(`/orgs/${orgId}/invoices`);
}

export interface McpOrg {
  id: string;
  name: string;
  role: string;
}

/** The signed-in user's orgs (for the MCP consent org picker). */
export function getMcpOrgs(): Promise<{ orgs: McpOrg[] }> {
  return apiFetch<{ orgs: McpOrg[] }>(`/mcp-connect/orgs`);
}

/** Record the org for the in-flight MCP connection (resolved by /v1 on first call). */
export function selectMcpOrg(organizationId: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/mcp-connect/select-org`, {
    method: "POST",
    body: JSON.stringify({ organizationId }),
  });
}

// ---------------------------------------------------------------------------
// SSO (enterprise) — capability + providers + "Require SSO". Provider register/
// verify/delete go straight to the native @better-auth/sso endpoints via the
// auth client; these are the product-policy bits.
// ---------------------------------------------------------------------------

export interface SsoProvider {
  id: string;
  providerId: string;
  domain: string;
  issuer: string;
  domainVerified: boolean;
}

export interface SsoStatus {
  /** Whether the org may CONFIGURE SSO (Enterprise plan or admin override). */
  enabled: boolean;
  /** Whether "Require SSO" is on for the org. */
  enforced: boolean;
  providers: SsoProvider[];
}

export function getSsoStatus(orgId: string): Promise<SsoStatus> {
  if (MOCK_ENABLED) {
    return Promise.resolve({ enabled: true, enforced: false, providers: [] });
  }
  return apiFetch<SsoStatus>(`/orgs/${orgId}/sso`);
}

export function setSsoEnforced(orgId: string, enforced: boolean): Promise<{ enforced: boolean }> {
  if (MOCK_ENABLED) return Promise.resolve({ enforced });
  return apiFetch<{ enforced: boolean }>(`/orgs/${orgId}/sso/enforce`, {
    method: "PUT",
    body: JSON.stringify({ enforced }),
  });
}

// ---------------------------------------------------------------------------
// BYOK — bring your own AI provider keys (Growth+)
// ---------------------------------------------------------------------------

/** Fetch the org's BYOK settings + configured providers (keys masked). */
export function getByokSettings(orgId: string): Promise<ByokSettings> {
  if (MOCK_ENABLED) {
    return Promise.resolve({ enabled: false, fallback: true, providers: [], available: true, configured: true, maxToolCalls: 10 });
  }
  return apiFetch<ByokSettings>(`/orgs/${orgId}/byok`);
}

/** Update the master enable + fallback toggle + (optionally) the tool-call cap. */
export function setByokSettings(
  orgId: string,
  body: { enabled: boolean; fallback: boolean; maxToolCalls?: number | null },
): Promise<{ enabled: boolean; fallback: boolean; maxToolCalls?: number | null }> {
  if (MOCK_ENABLED) return Promise.resolve(body);
  return apiFetch<{ enabled: boolean; fallback: boolean; maxToolCalls?: number | null }>(`/orgs/${orgId}/byok`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** Add a provider (the plaintext key is encrypted server-side). */
export function addByokProvider(orgId: string, body: ByokProviderWrite): Promise<ByokProviderRecord> {
  return apiFetch<ByokProviderRecord>(`/orgs/${orgId}/byok/providers`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Update a provider; omit `apiKey` to keep the stored key. */
export function updateByokProvider(
  orgId: string,
  id: string,
  body: Partial<ByokProviderWrite>,
): Promise<ByokProviderRecord> {
  return apiFetch<ByokProviderRecord>(`/orgs/${orgId}/byok/providers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteByokProvider(orgId: string, id: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/orgs/${orgId}/byok/providers/${id}`, { method: "DELETE" });
}

/** Set the try order (priority); `ids` is the desired order, index 0 = primary. */
export function reorderByokProviders(orgId: string, ids: string[]): Promise<ByokProviderRecord[]> {
  return apiFetch<ByokProviderRecord[]>(`/orgs/${orgId}/byok/providers/order`, {
    method: "PUT",
    body: JSON.stringify({ ids }),
  });
}

/** Test a SAVED provider with its stored key; persists last_test_ok. */
export function testByokProvider(orgId: string, id: string): Promise<{ ok: boolean; error?: string }> {
  return apiFetch<{ ok: boolean; error?: string }>(`/orgs/${orgId}/byok/providers/${id}/test`, { method: "POST" });
}

/** Test an UNSAVED config straight from the form (nothing persisted). */
export function testByokConfig(orgId: string, body: ByokProviderWrite): Promise<{ ok: boolean; error?: string }> {
  return apiFetch<{ ok: boolean; error?: string }>(`/orgs/${orgId}/byok/test`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
